import { useCallback, useEffect, useState } from "react";
import { listWorkflows, type WorkflowListing } from "../../../../api/content/workflowsApi";
import { isAbortError } from "../../../../api/http/httpClient";
import { readWorkflowSwitch, type OutputChoice, type SwitchWorkflow, type WorkflowSwitchOptions } from "../../../../api/threads/workflowSwitchApi";
import { toUiError, type UiError } from "../../../../errors/uiError";
import { loadAfterTypingPause } from "../../../../shared/loading/afterTypingPause";
import { useLoad, type LoadHandle } from "../../../../shared/loading/useLoad";

const NOTHING_CHOSEN: Readonly<Record<string, OutputChoice>> = {};
const NO_VALUES: Readonly<Record<string, unknown>> = {};

export interface SwitchOptionsRead {
  /** The last answer, kept in view while the next is read; `null` before the first. */
  readonly options: WorkflowSwitchOptions | null;
  /** Why the last read failed; `null` once one succeeds. */
  readonly error: UiError | null;
}

/**
 * What the Thread made so far and the Workflows it can switch to (F126), read again whenever
 * `revision` changes: the Workflow followed and its steps' progress, which change exactly when
 * a step makes something or the Workflow changes, not with every entry of a running turn. The
 * last answer stays in view meanwhile, as the step plans do (`useStepPlans`). `null`: nothing
 * is read.
 */
export function useSwitchOptions(threadId: string, revision: string | null): SwitchOptionsRead {
  const [read, setRead] = useState<{ readonly threadId: string; readonly result: SwitchOptionsRead } | null>(null);
  useEffect(() => {
    if (revision === null) return undefined;
    const controller = new AbortController();
    readWorkflowSwitch(threadId, { workflow: null, carried: NOTHING_CHOSEN, inputValues: NO_VALUES }, controller.signal).then(
      (options) => { setRead({ threadId, result: { options, error: null } }); },
      (caught: unknown) => {
        if (controller.signal.aborted || isAbortError(caught)) return;
        setRead((previous) => ({ threadId, result: { options: previous?.threadId === threadId ? previous.result.options : null, error: toUiError(caught) } }));
      },
    );
    return () => { controller.abort(); };
  }, [threadId, revision]);
  return read?.threadId === threadId ? read.result : NOT_READ;
}

const NOT_READ: SwitchOptionsRead = { options: null, error: null };

/** The Workflows of the Thread's content version, which the chooser groups and names (F97, F111). */
export function useThreadWorkflows(projectId: string, commit: string | null): LoadHandle<WorkflowListing> {
  const load = useCallback((signal: AbortSignal) => listWorkflows(projectId, commit, signal), [projectId, commit]);
  return useLoad(load);
}

export type PreparedSwitchState =
  | { readonly status: "idle" }
  | { readonly status: "loading"; readonly previous: SwitchWorkflow | null }
  | { readonly status: "loaded"; readonly value: SwitchWorkflow }
  | { readonly status: "failed"; readonly error: UiError };

interface PreparedSwitchInput {
  readonly threadId: string;
  /** `null` while no Workflow is picked: nothing is prepared. */
  readonly workflow: string | null;
  readonly carried: Readonly<Record<string, OutputChoice>>;
  readonly values: Readonly<Record<string, unknown>>;
  readonly lastEntryNumber: number;
}

type Answer = { readonly status: "loaded"; readonly value: SwitchWorkflow } | { readonly status: "failed"; readonly error: UiError };

/**
 * The picked Workflow prepared for this Thread with the person's choices so far: its steps'
 * plans with the carried outputs and the values given, read again once typing pauses. The last
 * answer stays in view while the next is read, as the start's does (`usePreparedStart`).
 */
export function usePreparedSwitch({ threadId, workflow, carried, values, lastEntryNumber }: PreparedSwitchInput): PreparedSwitchState {
  const request = workflow === null ? null : JSON.stringify([threadId, workflow, carried, values, lastEntryNumber]);
  const [answer, setAnswer] = useState<{ readonly request: string; readonly answer: Answer } | null>(null);
  const [lastLoaded, setLastLoaded] = useState<SwitchWorkflow | null>(null);
  useEffect(() => {
    if (request === null || workflow === null) return undefined;
    return loadAfterTypingPause(
      (signal) => readWorkflowSwitch(threadId, { workflow, carried, inputValues: values }, signal),
      (state) => {
        if (state.status === "failed") {
          setAnswer({ request, answer: state });
          return;
        }
        const prepared = state.value.workflows[0];
        if (prepared === undefined) return;
        setAnswer({ request, answer: { status: "loaded", value: prepared } });
        setLastLoaded(prepared);
      },
    );
  }, [request, threadId, workflow, carried, values]);
  if (request === null) return { status: "idle" };
  return answer?.request === request ? answer.answer : { status: "loading", previous: lastLoaded?.reference === workflow ? lastLoaded : null };
}
